import React, { useEffect, useState, useCallback } from 'react';
import { creditApi } from '../api/credit';
import { purchaseApi } from '../api/purchase';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

const CreditFollowUp: React.FC = () => {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const today = new Date().toISOString().split('T')[0];

  const [rows, setRows] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>({ debtors:0, total_owed:0, broken:0, due:0 });
  const [branches, setBranches] = useState<any[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<any>({ branch_id:'', salesperson_id:'', mine:false });

  const [rec, setRec] = useState<any>(null);       // the customer being contacted
  const [hist, setHist] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [cs, setCs] = useState<any>(null);   // credit-settings editor

  const canManage = hasPermission('crm.manage');
  const fmt = (n:any) => Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});

  const load = useCallback(() => {
    setLoading(true);
    creditApi.worklist({
      branch_id: filters.branch_id || undefined,
      salesperson_id: filters.salesperson_id || undefined,
      mine: filters.mine ? 1 : undefined,
    }).then(r => { setRows(r.data.data || []); setSummary(r.data.summary || {}); })
      .catch(() => {}).finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => {
    purchaseApi.branches().then(r => setBranches(r.data.data || [])).catch(() => {});
    creditApi.salespeople().then(r => setPeople(r.data.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openRecord = (row:any) => {
    setRec({ row, type:'call', notes:'', promise_amount:'', promise_date:'', next_follow_up:'', outcome:'' });
    setHist([]);
    creditApi.history(row.contact_id).then(r => setHist(r.data.data || [])).catch(() => {});
  };

  const saveRecord = async () => {
    if (!rec.notes.trim()) { showToast('Write what happened', 'error'); return; }
    setSaving(true);
    try {
      await creditApi.record({
        contact_id: rec.row.contact_id,
        type: rec.type,
        notes: rec.notes,
        promise_amount: rec.promise_amount ? Number(rec.promise_amount) : null,
        promise_date: rec.promise_date || null,
        next_follow_up: rec.next_follow_up || null,
        outcome: rec.outcome || null,
      });
      showToast('Logged'); setRec(null); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
    finally { setSaving(false); }
  };

  const openCredit = (row:any) => {
    creditApi.settings(row.contact_id).then(r => {
      const d = r.data.data || {};
      setCs({ row, credit_limit: d.credit_limit ?? '', payment_terms_days: d.payment_terms_days ?? '', credit_hold: !!d.credit_hold, credit_hold_reason: d.credit_hold_reason || '' });
    }).catch(() => setCs({ row, credit_limit:'', payment_terms_days:'', credit_hold:false, credit_hold_reason:'' }));
  };

  const saveCredit = async () => {
    setSaving(true);
    try {
      await creditApi.saveSettings(cs.row.contact_id, {
        credit_limit: cs.credit_limit ? Number(cs.credit_limit) : 0,
        payment_terms_days: cs.payment_terms_days ? Number(cs.payment_terms_days) : 0,
        credit_hold: !!cs.credit_hold,
        credit_hold_reason: cs.credit_hold ? (cs.credit_hold_reason || null) : null,
      });
      showToast('Saved'); setCs(null); load();
    } catch (e:any) { showToast(e.response?.data?.message || 'Could not save', 'error'); }
    finally { setSaving(false); }
  };

  const th: React.CSSProperties = { padding:'8px 10px', fontSize:10, fontWeight:600, color:'var(--tx3)', textTransform:'uppercase', textAlign:'left' };
  const types = [['call','Called'],['visit','Visited'],['whatsapp','WhatsApp'],['promise','Promised'],['paid','Paid'],['other','Other']];

  // ---------- credit settings ----------
  if (cs) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Credit settings — {cs.row.name}</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setCs(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveCredit} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      <div style={{ fontSize:13, color:'var(--tx2)', marginBottom:10 }}>Currently owes <span className="mono" style={{ fontWeight:700 }}>{fmt(cs.row.owed)}</span></div>
      <div className="card"><div className="card-body">
        <div className="form-grid-2">
          <div className="form-group"><label className="form-label">Credit limit</label>
            <input className="form-control mono" style={{textAlign:'right'}} placeholder="0 = no limit" value={cs.credit_limit} onChange={e => setCs({...cs, credit_limit:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Payment terms (days)</label>
            <input className="form-control mono" style={{textAlign:'right'}} placeholder="e.g. 30" value={cs.payment_terms_days} onChange={e => setCs({...cs, payment_terms_days:e.target.value})} /></div>
        </div>
        <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, margin:'4px 0 10px' }}>
          <input type="checkbox" checked={!!cs.credit_hold} onChange={e => setCs({...cs, credit_hold:e.target.checked})} /> Put on credit hold (block new credit sales)
        </label>
        {cs.credit_hold && (
          <div className="form-group"><label className="form-label">Hold reason</label>
            <input className="form-control" placeholder="Why" value={cs.credit_hold_reason} onChange={e => setCs({...cs, credit_hold_reason:e.target.value})} /></div>
        )}
        <div style={{ fontSize:11, color:'var(--tx3)' }}>Limit 0 means no limit. Terms set the due date on new invoices. On hold, new credit sales are blocked.</div>
      </div></div>
    </div>
  );

  // ---------- record panel ----------
  if (rec) return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Record contact — {rec.row.name}</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" onClick={() => setRec(null)}>Cancel</button>
          <button className="btn btn-primary" onClick={saveRecord} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      <div style={{ fontSize:13, color:'var(--tx2)', marginBottom:10 }}>
        Owes <span className="mono" style={{ fontWeight:700 }}>{fmt(rec.row.owed)}</span>
        {rec.row.days_overdue > 0 && <span style={{ color:'var(--red)', marginLeft:10 }}>{rec.row.days_overdue} days overdue</span>}
        {rec.row.mobile && <span style={{ color:'var(--tx3)', marginLeft:10 }}>{rec.row.mobile}</span>}
      </div>

      <div className="card"><div className="card-body">
        <div className="form-group"><label className="form-label">What happened *</label>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
            {types.map(([v,l]) => (
              <button key={v} className={'btn btn-sm ' + (rec.type===v ? 'btn-primary' : 'btn-secondary')} onClick={() => setRec({...rec, type:v})}>{l}</button>
            ))}
          </div>
          <textarea className="form-control" rows={2} placeholder="What was said / agreed" value={rec.notes} onChange={e => setRec({...rec, notes:e.target.value})} />
        </div>
        <div className="form-grid-3">
          <div className="form-group"><label className="form-label">Promised amount</label>
            <input className="form-control mono" style={{textAlign:'right'}} value={rec.promise_amount} onChange={e => setRec({...rec, promise_amount:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Promised by (date)</label>
            <input type="date" className="form-control" value={rec.promise_date} onChange={e => setRec({...rec, promise_date:e.target.value})} /></div>
          <div className="form-group"><label className="form-label">Next follow-up</label>
            <input type="date" className="form-control" value={rec.next_follow_up} onChange={e => setRec({...rec, next_follow_up:e.target.value})} /></div>
        </div>
      </div></div>

      <div className="card" style={{ marginTop:12 }}>
        <div className="card-header"><span>History</span><span className="record-count">{hist.length}</span></div>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'var(--sur2)' }}>
            <th style={th}>When</th><th style={th}>Type</th><th style={th}>Note</th><th style={{...th, textAlign:'right'}}>Promised</th><th style={th}>By</th><th style={th}>Next</th><th style={th}>Who</th>
          </tr></thead>
          <tbody>
            {hist.length === 0 ? <tr><td colSpan={7} style={{ padding:16, textAlign:'center', color:'var(--tx3)' }}>No contact logged yet.</td></tr>
            : hist.map((h:any) => (
              <tr key={h.id} style={{ borderBottom:'1px solid var(--bdr)' }}>
                <td style={{ padding:'6px 10px', fontSize:11, color:'var(--tx3)' }}>{String(h.created_at||'').slice(0,10)}</td>
                <td style={{ padding:'6px 10px', fontSize:12 }}>{h.type}</td>
                <td style={{ padding:'6px 10px', fontSize:12, color:'var(--tx2)' }}>{h.notes}</td>
                <td className="td-num" style={{ padding:'6px 10px' }}>{h.promise_amount ? fmt(h.promise_amount) : '-'}</td>
                <td style={{ padding:'6px 10px', fontSize:12 }}>{h.promise_date || '-'}</td>
                <td style={{ padding:'6px 10px', fontSize:12 }}>{h.next_follow_up || '-'}</td>
                <td style={{ padding:'6px 10px', fontSize:11, color:'var(--tx3)' }}>{h.user_name || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ---------- worklist ----------
  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Credit Follow-up</h1>
        <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
      </div>

      <div style={{ display:'flex', gap:14, marginBottom:12, flexWrap:'wrap' }}>
        <div className="card" style={{ flex:1, minWidth:120 }}><div className="card-body" style={{ padding:'10px 14px' }}>
          <div style={{ fontSize:11, color:'var(--tx3)' }}>Customers owing</div>
          <div style={{ fontSize:20, fontWeight:700 }}>{summary.debtors || 0}</div></div></div>
        <div className="card" style={{ flex:1, minWidth:120 }}><div className="card-body" style={{ padding:'10px 14px' }}>
          <div style={{ fontSize:11, color:'var(--tx3)' }}>Total owed</div>
          <div style={{ fontSize:20, fontWeight:700, color:'var(--red)' }} className="mono">{fmt(summary.total_owed)}</div></div></div>
        <div className="card" style={{ flex:1, minWidth:120 }}><div className="card-body" style={{ padding:'10px 14px' }}>
          <div style={{ fontSize:11, color:'var(--tx3)' }}>Due to chase</div>
          <div style={{ fontSize:20, fontWeight:700, color:'var(--amber)' }}>{summary.due || 0}</div></div></div>
        <div className="card" style={{ flex:1, minWidth:120 }}><div className="card-body" style={{ padding:'10px 14px' }}>
          <div style={{ fontSize:11, color:'var(--tx3)' }}>Broken promises</div>
          <div style={{ fontSize:20, fontWeight:700, color:'var(--red)' }}>{summary.broken || 0}</div></div></div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center', flexWrap:'wrap' }}>
        <select className="form-control" style={{ width:'auto' }} value={filters.branch_id} onChange={e => setFilters({...filters, branch_id:e.target.value})}>
          <option value="">All branches</option>
          {branches.map((b:any) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select className="form-control" style={{ width:'auto' }} value={filters.salesperson_id} onChange={e => setFilters({...filters, salesperson_id:e.target.value})}>
          <option value="">All salespeople</option>
          {people.map((p:any) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
          <input type="checkbox" checked={!!filters.mine} onChange={e => setFilters({...filters, mine:e.target.checked})} /> Mine
        </label>
        <span className="record-count" style={{ marginLeft:'auto' }}>{rows.length}</span>
      </div>

      <div className="card">
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead><tr style={{ background:'var(--sur2)' }}>
            <th style={th}>Customer</th><th style={{...th, textAlign:'right'}}>Owes</th><th style={{...th, textAlign:'right'}}>Overdue</th>
            <th style={th}>Last contact</th><th style={{...th, textAlign:'right'}}>Promised</th><th style={th}>By</th><th style={th}>Next</th><th></th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>Loading...</td></tr>
            : rows.length === 0 ? <tr><td colSpan={8} style={{ padding:24, textAlign:'center', color:'var(--tx3)' }}>Nobody owes — all clear.</td></tr>
            : rows.map((r:any) => (
              <tr key={r.contact_id} style={{ borderBottom:'1px solid var(--bdr)', background: r.broken ? 'rgba(220,38,38,0.06)' : undefined }}>
                <td style={{ padding:'8px 10px', fontSize:12, fontWeight:600 }}>
                  {r.name}
                  {r.on_hold && <span className="badge badge-red" style={{ marginLeft:6 }}>hold</span>}
                  {r.mobile && <div style={{ fontSize:11, color:'var(--tx3)' }}>{r.mobile}</div>}
                </td>
                <td className="td-num" style={{ padding:'8px 10px', fontWeight:600 }}>{fmt(r.owed)}</td>
                <td className="td-num" style={{ padding:'8px 10px', color: r.days_overdue > 0 ? 'var(--red)' : 'var(--tx3)' }}>{r.days_overdue > 0 ? r.days_overdue + 'd' : '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:11, color:'var(--tx3)' }}>
                  {r.last_contact ? <>{String(r.last_contact).slice(0,10)}{r.last_outcome ? ' · ' + r.last_outcome : ''}</> : 'never'}
                </td>
                <td className="td-num" style={{ padding:'8px 10px' }}>{r.promise_amount ? fmt(r.promise_amount) : '-'}</td>
                <td style={{ padding:'8px 10px', fontSize:12, color: r.broken ? 'var(--red)' : 'var(--tx2)', fontWeight: r.broken ? 700 : 400 }}>
                  {r.promise_date || '-'}{r.broken ? ' · broken' : ''}
                </td>
                <td style={{ padding:'8px 10px', fontSize:12, color: r.due ? 'var(--amber)' : 'var(--tx3)' }}>{r.next_follow_up || (r.due ? 'now' : '-')}</td>
                <td style={{ padding:'6px 10px', textAlign:'right', whiteSpace:'nowrap' }}>
                  {canManage && <button className="btn btn-primary btn-sm" onClick={() => openRecord(r)}>Record</button>}
                  {canManage && <button className="btn btn-secondary btn-sm" style={{ marginLeft:6 }} onClick={() => openCredit(r)}>Limit</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CreditFollowUp;
